import { catalogPage } from '@/shared/config/pageCatalog';
import { ROUTES } from '@/shared/config/routes';
import { STATUS_TAB_PARAM } from '@/shared/lib/hooks/useStatusTabFromUrl';
import { RpmStatusTypes } from '@/shared/lib/types/remotePatientMonitoring';
import type { ResultCard } from './resultCards';

/** A page of the portal (shared/config/pageCatalog), on one of its tabs. */
export type PageLink = { path: string; to: string };

/**
 * A link to the page, on the tab when the page has one for it; the page alone otherwise.
 * Null for anything that is not a page of the portal, so the agent can only name real pages:
 * its list is the same catalog, published by CI (scripts/export-ai-search-pages.ts).
 */
export const linkForPage = (path: unknown, tab?: unknown): PageLink | null => {
  const page = catalogPage(path);
  if (!page) return null;

  const onTab = typeof tab === 'string' && page.tabs.includes(tab);
  return { path: page.path, to: onTab ? `${page.path}?${STATUS_TAB_PARAM}=${tab}` : page.path };
};

const rpmStatusOf = (payload: Record<string, unknown> | null) => {
  const filterOptions = payload?.filterOptions as Record<string, unknown> | undefined;
  return filterOptions?.rpmStatus;
};

/** Root fields whose full list has a page, by the query's arguments (viewAllPayload). */
const RESULT_PAGES: Record<string, (payload: Record<string, unknown> | null) => PageLink | null> = {
  getRpmEnrolledAccounts: () => linkForPage(ROUTES.CARE_MANAGEMENT.PATIENTS, RpmStatusTypes.ENROLLED),
  getCareManagementEnrolledAccounts: () => linkForPage(ROUTES.CARE_MANAGEMENT.PATIENTS, RpmStatusTypes.ENROLLED),
  getCareManagementUnenrolledAccounts: () => linkForPage(ROUTES.CARE_MANAGEMENT.PATIENTS, RpmStatusTypes.UNENROLLED),
  getAccountsByRpmStatus: payload => linkForPage(ROUTES.CARE_MANAGEMENT.PATIENTS, rpmStatusOf(payload)),
  accountsSearch: () => linkForPage(ROUTES.PATIENTS.LOOKUP),
  getRpmAlerts: () => linkForPage(ROUTES.REMOTE_PATIENT_MONITORING.ALERTS),
  getProviderApprovalAccounts: () => linkForPage(ROUTES.REMOTE_PATIENT_MONITORING.PROVIDER_APPROVALS),
  getRpmProviderSignoffs: () => linkForPage(ROUTES.REPORTS.RPM_PROVIDER_SIGNOFFS)
};

/**
 * For any other query, by what came back: a list of patients is on the patients page, on the tab
 * of their status when they all share one.
 */
export const linkForPatients = (cards: ResultCard[]): PageLink | null => {
  if (cards.length === 0 || !cards.every(card => card.truentityId)) return null;

  const statuses = new Set(cards.map(card => card.rpmStatus));
  return linkForPage(ROUTES.CARE_MANAGEMENT.PATIENTS, statuses.size === 1 ? [...statuses][0] : undefined);
};

/** The page for the root fields this knows, by the query's arguments; null for the rest. */
export const linkForResult = (rootField: string | null, viewAllPayload: Record<string, unknown> | null): PageLink | null =>
  (rootField && Object.keys(RESULT_PAGES).includes(rootField) && RESULT_PAGES[rootField](viewAllPayload)) || null;
